/**
 * InteractionCard
 *
 * One conversation on a CRM timeline. Renders BOTH shapes a CustomerInteraction
 * can take:
 *   - hand-logged (PHONE_CALL/DELIVERY/etc. typed by staff) -> `notes`
 *   - RingCentral call pipeline -> summary + structured fields, `notes` EMPTY
 *
 * The previous timeline rendered only `notes`, so every pipeline call showed a
 * header and a blank body. Everything below reads the pipeline fields first and
 * falls back to `notes`.
 *
 * Styling is deliberately inline style objects with plain elements - no MUI, no
 * utility classes - so the same card drops into the MUI CustomerCRM page and the
 * plain-CSS Bridge Club member page without inheriting either one's system.
 */

// ==== STANDALONE:FARMHAND_PRO:START ====
// Feature: Conversation timeline card with call-transcript disclosure.
// Tier: Pro (call-transcript pipeline is a Pro add-on)
// Dependencies: services/customerCrmAPI (CustomerInteraction type only)
// Generalization: colors are literals here; a standalone build would take them
//                 from a tenant theme object.
// ============================================

import React, { useState } from 'react';
import {
  CustomerInteraction, InteractionCommitment, InteractionTask, customerCRMAPI,
} from '../services/customerCrmAPI';

const C = {
  text: '#111827',
  muted: '#6b7280',
  border: '#e5e7eb',
  card: '#ffffff',
  subtle: '#f9fafb',
  primary: '#059669',
  primarySoft: '#ecfdf5',
  warnText: '#92400e',
  warnBg: '#fffbeb',
  warnBorder: '#fcd34d',
  dangerText: '#b91c1c',
  dangerBg: '#fef2f2',
  dangerBorder: '#fecaca',
};

interface Props {
  interaction: CustomerInteraction;
  /** Highlight treatment for the most recent entry. */
  highlight?: boolean;
  /** Optional label above the header, e.g. "Last Conversation". */
  label?: string;
}

// ── formatting helpers ───────────────────────────────────────────────────────

const formatWhen = (iso: string | null): string => {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Unknown date';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const formatDuration = (seconds?: number | null): string | null => {
  if (seconds === null || seconds === undefined || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatDueDate = (due?: string | null): string | null => {
  if (!due) return null;
  const d = new Date(due);
  if (isNaN(d.getTime())) return due; // show whatever the model gave us
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** The summarizer is asked for {text, due_date} but it is an LLM - tolerate a bare string. */
const commitmentText = (c: InteractionCommitment | string): string =>
  typeof c === 'string' ? c : (c?.text || '');

const commitmentDue = (c: InteractionCommitment | string): string | null =>
  typeof c === 'string' ? null : formatDueDate(c?.due_date);

/** The unformatted due date, for sending back to the API. */
const commitmentRawDue = (c: InteractionCommitment | string): string | null =>
  typeof c === 'string' ? null : (c?.due_date || null);

/** Default when the model extracted no due date - short on purpose, so an unscheduled
 *  promise surfaces soon and gets re-dated by a human rather than parked. */
const defaultDue = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

const SENTIMENT_STYLE: Record<string, { bg: string; fg: string; border: string; label: string }> = {
  positive: { bg: C.primarySoft, fg: '#047857', border: '#a7f3d0', label: 'Positive' },
  neutral: { bg: C.subtle, fg: C.muted, border: C.border, label: 'Neutral' },
  negative: { bg: C.dangerBg, fg: C.dangerText, border: C.dangerBorder, label: 'Negative' },
};

// ── small presentational pieces ──────────────────────────────────────────────

const Pill: React.FC<{ bg: string; fg: string; border: string; children: React.ReactNode }> = ({
  bg, fg, border, children,
}) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 600,
      lineHeight: 1.6,
      backgroundColor: bg,
      color: fg,
      border: `1px solid ${border}`,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const TagRow: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <div style={{ marginTop: 10 }}>
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
      {title}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((t, i) => (
        <span
          key={`${t}-${i}`}
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: '0.75rem',
            backgroundColor: C.subtle,
            color: C.text,
            border: `1px solid ${C.border}`,
          }}
        >
          {t}
        </span>
      ))}
    </div>
  </div>
);

/**
 * One commitment's "create task" control.
 *
 * Deliberately a CONFIRM step, not an auto-create. A wrong task appearing in the Tasks tab
 * on its own teaches people to distrust the tab, which costs more than the click saves.
 * The AI proposes; a human commits.
 */
const CommitmentAction: React.FC<{
  interactionId: number;
  text: string;
  rawDue: string | null;
  existing?: InteractionTask;
  onCreated: (task: InteractionTask) => void;
}> = ({ interactionId, text, rawDue, existing, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(text);
  const [due, setDue] = useState(rawDue || defaultDue());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (existing) {
    return (
      <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#047857', fontWeight: 600 }}>
        {'\u2713'} Task created
        {existing.assigned_to ? ` \u00b7 ${existing.assigned_to}` : ''}
        {existing.due_date ? ` \u00b7 due ${formatDueDate(existing.due_date)}` : ''}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          marginTop: 4,
          minHeight: 32,
          padding: '4px 10px',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#047857',
          backgroundColor: '#ffffff',
          border: '1px solid #a7f3d0',
          borderRadius: 6,
          cursor: 'pointer',
        }}
      >
        + Create task
      </button>
    );
  }

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError('A title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await customerCRMAPI.createTaskFromCall(interactionId, {
        title: trimmed,
        due_date: due || null,
      });
      onCreated(res.data);
      setOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not create the task.');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    minHeight: 36,
    padding: '6px 8px',
    fontSize: '0.82rem',
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    marginBottom: 6,
    boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: C.muted,
    marginBottom: 2,
  };

  return (
    <div
      style={{
        marginTop: 6,
        padding: 8,
        backgroundColor: '#ffffff',
        border: `1px solid ${C.border}`,
        borderRadius: 6,
      }}
    >
      <label style={labelStyle}>Task</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} style={fieldStyle} />
      <label style={labelStyle}>Due</label>
      <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={fieldStyle} />
      <div style={{ fontSize: '0.7rem', color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>
        Assigned to whoever took the call, when that can be resolved - otherwise to you.
      </div>
      {error && (
        <div style={{ fontSize: '0.75rem', color: C.dangerText, marginBottom: 6 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          style={{
            flex: 1,
            minHeight: 36,
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#ffffff',
            backgroundColor: saving ? C.muted : C.primary,
            border: 'none',
            borderRadius: 6,
            cursor: saving ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Creating...' : 'Create task'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          style={{
            minHeight: 36,
            padding: '0 12px',
            fontSize: '0.8rem',
            color: C.text,
            backgroundColor: C.subtle,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── main component ───────────────────────────────────────────────────────────

const InteractionCard: React.FC<Props> = ({ interaction, highlight = false, label }) => {
  const [expanded, setExpanded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  // Tasks created in this session, merged with those the server already knew about, so a
  // commitment flips to "created" without refetching the whole timeline.
  const [localTasks, setLocalTasks] = useState<InteractionTask[]>([]);

  const i = interaction;
  const allTasks = [...(i.tasks || []), ...localTasks];
  /** Match a commitment to an existing task by title - the same text the server stored.
   *  Editing the title before saving breaks the match, which can only ever offer a second
   *  task, never create a silent duplicate: the server dedupes on title as well. */
  const taskFor = (text: string): InteractionTask | undefined =>
    allTasks.find((t) => t.title.trim() === text.trim());

  // Only render values that are actually strings/arrays - a pipeline row can have
  // any subset of these populated depending on where it stopped.
  const summary = (i.summary || '').trim();
  const notes = (i.notes || '').trim();
  const topics = Array.isArray(i.topics) ? i.topics.filter(Boolean).map(String) : [];
  const products = Array.isArray(i.products) ? i.products.filter(Boolean).map(String) : [];
  const commitments = (Array.isArray(i.commitments) ? i.commitments : [])
    .filter((c) => commitmentText(c).trim().length > 0);

  // Labeled transcript is an INTERPRETATION; the raw one is verbatim. Prefer the
  // labeled version, fall back to raw so an unlabeled call is still readable.
  const labeled = (i.transcript_labeled || '').trim();
  const raw = (i.transcription || '').trim();
  const transcript = labeled || raw;
  const transcriptIsLabeled = labeled.length > 0;

  const isCall = i.interaction_type === 'PHONE_CALL';
  // Present on every pipeline call, matched or not. On an unmatched call it is the only
  // identifying information there is, so it always renders.
  const externalNumber = (i.external_number_e164 || '').trim();
  const duration = formatDuration(i.duration_seconds);
  const direction = i.call_direction === 'inbound' ? 'Inbound'
    : i.call_direction === 'outbound' ? 'Outbound' : null;

  const lowQuality = i.transcript_quality === 'poor' || i.transcript_quality === 'fair';
  const summaryFailed = i.summary_status === 'failed';

  // Body text for the collapsed card. Pipeline calls have no notes; hand-logged
  // interactions have no summary. Show whichever exists, summary first.
  const bodyText = summary || notes;

  const hasExpandableContent =
    commitments.length > 0 ||
    topics.length > 0 ||
    products.length > 0 ||
    transcript.length > 0 ||
    (!!i.outcome && i.outcome.trim().length > 0) ||
    // notes AND summary both present -> notes is extra content worth revealing
    (summary.length > 0 && notes.length > 0);

  const sentiment = i.sentiment ? SENTIMENT_STYLE[i.sentiment] : undefined;

  const toggle = () => {
    if (hasExpandableContent) setExpanded((v) => !v);
  };

  return (
    <div
      style={{
        border: `1px solid ${highlight ? '#a7f3d0' : C.border}`,
        borderRadius: 8,
        backgroundColor: highlight ? C.primarySoft : C.card,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      {/* ── Clickable header + collapsed body ─────────────────────────────── */}
      <div
        role={hasExpandableContent ? 'button' : undefined}
        tabIndex={hasExpandableContent ? 0 : undefined}
        aria-expanded={hasExpandableContent ? expanded : undefined}
        onClick={toggle}
        onKeyDown={(e) => {
          if (!hasExpandableContent) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        style={{
          padding: 12,
          minHeight: 44,
          cursor: hasExpandableContent ? 'pointer' : 'default',
          // Long transcripts/summaries must never push the column sideways.
          overflowWrap: 'anywhere',
        }}
      >
        {label && (
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
            {label}
          </div>
        )}

        {/* Title row - wraps rather than overflowing at 375px */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 700, color: C.text }}>
            {i.interaction_type_display || 'Interaction'}
          </span>
          {direction && (
            <Pill bg={C.subtle} fg={C.muted} border={C.border}>
              {direction}
            </Pill>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: C.muted, whiteSpace: 'nowrap' }}>
            {formatWhen(i.interaction_date)}
          </span>
        </div>

        {/* Meta row */}
        <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: bodyText || lowQuality ? 8 : 0 }}>
          {/* Who took it. The extension mapping is stated plainly; a transcript guess is
              hedged, because a wrong name is worse than no name. */}
          {i.handled_by ? (
            <>Handled by <strong style={{ color: C.text }}>{i.handled_by}</strong></>
          ) : i.handled_by_inferred ? (
            <>Handled by <strong style={{ color: C.text }}>likely {i.handled_by_inferred}</strong>
              <span style={{ fontSize: '0.72rem' }}> (from the transcript)</span></>
          ) : (
            <>{isCall ? 'Logged by' : 'Caller'}: <strong style={{ color: C.text }}>{i.caller || 'Unknown'}</strong></>
          )}
          {externalNumber && <> &nbsp;|&nbsp; <strong style={{ color: C.text }}>{externalNumber}</strong></>}
          {duration && <> &nbsp;|&nbsp; Duration: <strong style={{ color: C.text }}>{duration}</strong></>}
          {i.related_order_number && <> &nbsp;|&nbsp; Order #{i.related_order_number}</>}
        </div>

        {/* Transcript-quality warning sits ABOVE the summary on purpose: a fluent
            summary built on a garbled transcript must not read as trustworthy. */}
        {lowQuality && (
          <div
            style={{
              backgroundColor: C.warnBg,
              border: `1px solid ${C.warnBorder}`,
              borderRadius: 6,
              padding: '8px 10px',
              marginBottom: 8,
              fontSize: '0.78rem',
              color: C.warnText,
              lineHeight: 1.45,
            }}
          >
            <strong>
              {i.transcript_quality === 'poor' ? 'Poor audio quality' : 'Fair audio quality'}
            </strong>
            {' — this transcript may be unreliable. Verify anything below against the '}
            {'recording before acting on it.'}
            {typeof i.transcript_coverage === 'number' && i.transcript_coverage < 0.999 && (
              <>
                {' '}Only <strong>{Math.round(i.transcript_coverage * 100)}%</strong> of the
                audio was transcribed, so part of this call is missing.
              </>
            )}
          </div>
        )}

        {i.possible_issue && i.possible_issue.trim() && (
          <div
            style={{
              backgroundColor: C.dangerBg,
              border: `1px solid ${C.dangerBorder}`,
              borderRadius: 6,
              padding: '8px 10px',
              marginBottom: 8,
              fontSize: '0.8rem',
              color: C.dangerText,
              lineHeight: 1.45,
            }}
          >
            <strong>Possible issue</strong> — {i.possible_issue}
          </div>
        )}

        {summaryFailed && (
          <div
            style={{
              backgroundColor: C.warnBg,
              border: `1px solid ${C.warnBorder}`,
              borderRadius: 6,
              padding: '8px 10px',
              marginBottom: 8,
              fontSize: '0.78rem',
              color: C.warnText,
            }}
          >
            <strong>Summary unavailable</strong> — the AI summary failed for this call. The
            transcript below is intact.
          </div>
        )}

        {bodyText ? (
          <p style={{ margin: 0, fontSize: '0.875rem', color: C.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {bodyText}
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: '0.85rem', color: C.muted, fontStyle: 'italic' }}>
            {i.pipeline_status && i.pipeline_status !== 'done'
              ? `Call processing (${i.pipeline_status})…`
              : 'No summary or notes recorded.'}
          </p>
        )}

        {/* Status pills + expand affordance */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 10 }}>
          {sentiment && (
            <Pill bg={sentiment.bg} fg={sentiment.fg} border={sentiment.border}>
              {sentiment.label}
            </Pill>
          )}
          {i.follow_up_needed && (
            <Pill bg={C.warnBg} fg={C.warnText} border={C.warnBorder}>
              Follow-up needed
            </Pill>
          )}
          {commitments.length > 0 && (
            <Pill bg={C.primarySoft} fg="#047857" border="#a7f3d0">
              {commitments.length} commitment{commitments.length === 1 ? '' : 's'}
            </Pill>
          )}
          {hasExpandableContent && (
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', fontWeight: 600, color: C.primary, whiteSpace: 'nowrap' }}>
              {expanded ? 'Hide details ▲' : 'Show details ▼'}
            </span>
          )}
        </div>
      </div>

      {/* ── Expanded detail ───────────────────────────────────────────────── */}
      {expanded && hasExpandableContent && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: 12, backgroundColor: C.card }}>
          {/* Commitments first and loudest - this is what someone actually has to act on. */}
          {commitments.length > 0 && (
            <div
              style={{
                borderLeft: `4px solid ${C.primary}`,
                backgroundColor: C.primarySoft,
                borderRadius: 6,
                padding: '10px 12px',
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Commitments we made
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {commitments.map((c, idx) => {
                  const text = commitmentText(c);
                  const due = commitmentDue(c);
                  const existing = taskFor(text);
                  return (
                    <li key={idx} style={{ fontSize: '0.875rem', color: C.text, lineHeight: 1.5, marginBottom: 8 }}>
                      {text}
                      {due && (
                        <span style={{ marginLeft: 6, fontSize: '0.75rem', fontWeight: 600, color: '#047857' }}>
                          (due {due})
                        </span>
                      )}
                      <CommitmentAction
                        interactionId={i.id}
                        text={text}
                        rawDue={commitmentRawDue(c)}
                        existing={existing}
                        onCreated={(t) => setLocalTasks((prev) => [...prev, t])}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {i.outcome && i.outcome.trim() && (
            <div style={{ fontSize: '0.85rem', color: C.text, marginBottom: 10 }}>
              <span style={{ fontWeight: 700 }}>Outcome: </span>
              {i.outcome}
            </div>
          )}

          {/* A pipeline call normally has no notes; if a human ALSO typed some, show them. */}
          {summary && notes && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                Staff notes
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: C.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {notes}
              </p>
            </div>
          )}

          {topics.length > 0 && <TagRow title="Topics" items={topics} />}
          {products.length > 0 && <TagRow title="Products" items={products} />}

          {/* Transcript is long - stays behind its own toggle. */}
          {transcript && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setShowTranscript((v) => !v)}
                style={{
                  width: '100%',
                  minHeight: 44,
                  padding: '10px 12px',
                  textAlign: 'left',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: C.text,
                  backgroundColor: C.subtle,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {showTranscript ? 'Hide transcript ▲' : 'Show transcript ▼'}
                <span style={{ fontWeight: 400, color: C.muted }}>
                  {transcriptIsLabeled ? ' (speaker-labeled)' : ' (raw, unlabeled)'}
                </span>
              </button>

              {showTranscript && (
                <>
                  {transcriptIsLabeled && (
                    <div style={{ fontSize: '0.72rem', color: C.muted, margin: '8px 2px 0', lineHeight: 1.4 }}>
                      Speaker labels are inferred by AI from call direction and party context,
                      not from voice separation. Treat attribution as a best guess.
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: 8,
                      padding: 10,
                      maxHeight: 320,
                      overflowY: 'auto',
                      overflowX: 'auto',
                      backgroundColor: C.subtle,
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      fontSize: '0.82rem',
                      lineHeight: 1.55,
                      color: C.text,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {transcript}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Retention purge keeps the summary and drops the verbatim text - say so
              rather than leaving the reader wondering where the transcript went. */}
          {!transcript && i.transcript_purged && (
            <div style={{ marginTop: 12, fontSize: '0.8rem', color: C.muted, fontStyle: 'italic' }}>
              The verbatim transcript for this call has been purged by retention policy. The
              summary above is retained.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InteractionCard;
// ==== STANDALONE:FARMHAND_PRO:END ====
